/* eslint-disable max-len */
import React, { useEffect, useRef, useState } from 'react';
import {
  VideoAssignmentSubmission,
  Video,
  VideoAssignmentSubmissionStatus,
} from 'types/VideoAssignmentSubmission';

import { useStore } from 'store';
import { generateWatchLink } from 'utils/psaHelpers';
import { PendingOrFailedPsaActions } from './PendingOrFailedPsaActions';
import { LinkButton } from './link-button';

export const PSA_ID_KEY = 'psa_id';

const getCurrentPsaId = () => {
  const currentPsaId = localStorage.getItem(PSA_ID_KEY);
  if (currentPsaId) {
    return currentPsaId;
  }
  return '';
};

interface PSAActionsProps {
  submission: VideoAssignmentSubmission;
  title: string;
  selectedPSAVideo: string | null;
  maxLengthInSeconds: number;
  loading: boolean;
  setLoadingState: (value: boolean) => void;
}

export const PSAActions = ({
  submission,
  title,
  selectedPSAVideo,
  maxLengthInSeconds,
  loading,
  setLoadingState,
}: PSAActionsProps) => {
  const [approvedVideo, setApprovedVideo] = useState<Video>(undefined);
  const [currentPsaLoadingId] = useState(getCurrentPsaId());
  const isMountedRef = useRef(true);

  const {
    rootStore: { classDetailsStore, videoAssignmentSubmissionStore },
  } = useStore();

  const { activeClass } = classDetailsStore;

  const createNewAssignmentSubmission = () => {
    setLoadingState(true);

    videoAssignmentSubmissionStore
      .addVideoAssignmentSubmission(activeClass.id, submission.assignmentId)
      .then(() => {
        if (isMountedRef) {
          videoAssignmentSubmissionStore.loadVideoAssignmentSubmissions().then(() => {
            if (isMountedRef) {
              setLoadingState(false);
            }
          });
        }
      });
  };

  useEffect(() => {
    if (
      submission.status === VideoAssignmentSubmissionStatus.COMPLETED &&
      submission.submittedVideoId
    ) {
      const submittedVideo: Video = submission.videos.find(
        (video) => video.id === submission.submittedVideoId
      );
      if (submittedVideo) {
        setApprovedVideo(submittedVideo);
      }
    }
  }, [submission]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  if (submission.status === VideoAssignmentSubmissionStatus.COMPLETED && approvedVideo) {
    const watchLink = generateWatchLink({
      videoId: approvedVideo.id,
      userId: submission.userId,
      videoPath: approvedVideo.clips?.[0]?.path,
      assignmentId: submission.assignmentId,
    });

    return (
      <LinkButton
        as="a"
        variant="watch"
        target="_blank"
        href={watchLink}
        rel="noreferrer"
        aria-label={`Play lesson ${title}`}
      >
        Watch
      </LinkButton>
    );
  }

  if (
    submission.status === VideoAssignmentSubmissionStatus.PENDING ||
    submission.status === VideoAssignmentSubmissionStatus.FAILED
  ) {
    return (
      <PendingOrFailedPsaActions
        submission={submission}
        userId={submission.userId}
        classId={activeClass.id}
        maxLengthInSeconds={maxLengthInSeconds}
        selectedPSAVideo={selectedPSAVideo}
        setLoadingState={setLoadingState}
        currentPsaLoadingId={currentPsaLoadingId}
        loading={loading}
      />
    );
  }

  return (
    submission.objectType === 'video-assignment' && (
      <LinkButton
        as="button"
        variant="submit"
        onClick={() => {
          createNewAssignmentSubmission();
        }}
        aria-label="Initiate Session"
      >
        Initiate Session
      </LinkButton>
    )
  );
};
